import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, AlertTriangle, Wrench, Info } from "lucide-react";
import { Footer } from "@/components/Footer";
import { currentIncident, incidentHistory, systems } from "@/lib/status";

export const Route = createFileRoute("/status")({
  head: () => ({
    meta: [
      { title: "System Status — LendyTrack" },
      {
        name: "description",
        content: "Live status of LendyTrack systems, current incidents, and recent incident history.",
      },
      { property: "og:title", content: "LendyTrack System Status" },
      { property: "og:url", content: "/status" },
    ],
    links: [{ rel: "canonical", href: "/status" }],
  }),
  component: StatusPage,
});

function StatusPage() {
  const allGood = !currentIncident && systems.every((s) => s.status === "operational");

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to="/" className="font-display text-lg font-semibold">LendyTrack</Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/faq" className="text-muted-foreground hover:text-foreground">FAQ</Link>
            <Link to="/contact" className="text-muted-foreground hover:text-foreground">Contact</Link>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-16">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Status</div>
        <h1 className="mt-2 font-display text-4xl font-semibold tracking-tight">System status</h1>

        <div
          className={`mt-6 flex items-center gap-3 rounded-2xl border p-5 shadow-soft ${
            allGood ? "border-success/30 bg-success/5" : "border-warning/40 bg-warning/10"
          }`}
        >
          {allGood ? (
            <CheckCircle2 className="h-6 w-6 text-success" />
          ) : (
            <AlertTriangle className="h-6 w-6 text-warning-foreground" />
          )}
          <div>
            <div className="font-display text-lg font-semibold">
              {allGood ? "All systems operational" : currentIncident?.title ?? "Service disruption"}
            </div>
            {currentIncident && (
              <p className="text-sm text-muted-foreground">{currentIncident.message}</p>
            )}
          </div>
        </div>

        <section className="mt-10">
          <h2 className="font-display text-xl font-semibold">Components</h2>
          <div className="mt-4 divide-y divide-border rounded-2xl border border-border bg-card shadow-soft">
            {systems.map((s) => (
              <div key={s.name} className="flex items-center justify-between px-5 py-3 text-sm">
                <span className="font-medium">{s.name}</span>
                <ComponentBadge status={s.status} />
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="font-display text-xl font-semibold">Recent incidents</h2>
          {incidentHistory.length === 0 ? (
            <p className="mt-4 rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
              No incidents reported in the last 90 days.
            </p>
          ) : (
            <ul className="mt-4 space-y-3">
              {incidentHistory.map((i) => (
                <li key={i.id} className="rounded-xl border border-border bg-card p-4 shadow-soft">
                  <div className="flex items-center gap-2">
                    <IncidentIcon severity={i.severity} />
                    <div className="font-semibold">{i.title}</div>
                    <span className="ml-auto text-xs uppercase tracking-widest text-muted-foreground">
                      {i.severity}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{i.message}</p>
                  <div className="mt-2 text-xs text-muted-foreground">
                    {new Date(i.startedAt).toLocaleString()}
                    {i.resolvedAt && <> — resolved {new Date(i.resolvedAt).toLocaleString()}</>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <p className="mt-10 text-xs text-muted-foreground">
          For urgent issues, contact{" "}
          <a href="mailto:support@lendytrack.com" className="font-semibold text-foreground hover:underline">
            support@lendytrack.com
          </a>
          .
        </p>
      </main>

      <Footer />
    </div>
  );
}

function ComponentBadge({ status }: { status: "operational" | "degraded" | "outage" | "maintenance" }) {
  const map = {
    operational: { label: "Operational", className: "bg-success/10 text-success border-success/30" },
    degraded: { label: "Degraded", className: "bg-warning/10 text-warning-foreground border-warning/40" },
    outage: { label: "Outage", className: "bg-danger/10 text-danger border-danger/40" },
    maintenance: { label: "Maintenance", className: "bg-accent/10 text-accent border-accent/40" },
  } as const;
  const t = map[status];
  return (
    <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${t.className}`}>
      {t.label}
    </span>
  );
}

function IncidentIcon({ severity }: { severity: string }) {
  if (severity === "resolved") return <CheckCircle2 className="h-4 w-4 text-success" />;
  if (severity === "maintenance") return <Wrench className="h-4 w-4 text-accent" />;
  if (severity === "info") return <Info className="h-4 w-4 text-accent" />;
  return <AlertTriangle className="h-4 w-4 text-warning-foreground" />;
}
